import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useConnectWallet } from "@web3-onboard/react";
import { providers } from "ethers";
import { web3Onboard } from "../../services/web3Onboard";
import { checkAndSwitchNetwork } from "../../utils/network";

interface MetaMaskContextType {
  address: string;
  connectWalletMeta: () => Promise<void>;
  disconnectWallet: () => Promise<void>;
  ethersProvider: providers.Web3Provider | null;
  connecting: boolean;
}

const MetaMaskContext = createContext<MetaMaskContextType | undefined>(
  undefined
);

interface MetaMaskProviderProps {
  children: ReactNode;
}

export const MetaMaskProvider: React.FC<MetaMaskProviderProps> = ({
  children,
}) => {
  const [{ wallet, connecting }, connect, disconnect] = useConnectWallet();
  const [address, setAddress] = useState<string>("");
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const ethersProviderRef = useRef<providers.Web3Provider | null>(null);

  useEffect(() => {
    const savedAddress = localStorage.getItem("walletAddress");
    if (savedAddress && window.ethereum) {
      connectWalletMeta();
    }
  }, []);

  useEffect(() => {
    if (wallet?.accounts.length && wallet.provider) {
      const addr = wallet.accounts[0].address;
      setAddress(addr);
      ethersProviderRef.current = new providers.Web3Provider(
        wallet.provider as any
      );

      localStorage.setItem("walletAddress", addr);
      checkNetwork();
    } else {
      setAddress("");
      ethersProviderRef.current = null;
      localStorage.removeItem("walletAddress");
    }
  }, [wallet]);

  console.log("This is address:", address);

  const checkNetwork = async () => {
    try {
      if (!wallet?.provider)
        throw new Error("Wallet provider is not available");

      const chainId = await wallet.provider.request({ method: "eth_chainId" });
      console.log("Current Chain ID:", chainId);

      if (chainId !== "0x89") {
        console.log("Switching to Polygon...");
        // await checkAndSwitchNetwork(wallet.provider, "0x89");
        const isCorrectNetwork = await checkAndSwitchNetwork(
          wallet.provider,
          "0x89"
        );

        if (!isCorrectNetwork) {
          console.warn(
            "Not connected to the correct network. Disconnecting..."
          );
          await disconnectWallet();
        }
        const newChainId = await wallet.provider.request({
          method: "eth_chainId",
        });
        console.log("New Chain ID after switch:", newChainId);

        if (newChainId !== "0x89") {
          throw new Error("Failed to switch to the IDEA Testnet");
        }
      }
    } catch (error) {
      console.error(error);
      await disconnectWallet();
      // handleError("Network check failed: " + error.message);
    }
  };

  const connectWalletMeta = async () => {
    setIsConnecting(true);
    try {
      await connect();
    } catch (err) {
      console.error("Failed to connect:", err);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = async () => {
    if (wallet) {
      await disconnect(wallet);
    }
    setAddress("");
    ethersProviderRef.current = null;
    localStorage.removeItem("walletAddress");
  };

  const value: MetaMaskContextType = {
    address,
    connectWalletMeta,
    disconnectWallet,
    ethersProvider: ethersProviderRef.current,
    connecting: isConnecting || connecting,
  };

  return (
    <MetaMaskContext.Provider value={value}>
      {children}
    </MetaMaskContext.Provider>
  );
};

export const useMetaMask = (): MetaMaskContextType => {
  const context = useContext(MetaMaskContext);
  if (!context) {
    throw new Error("useMetaMask must be used within a MetaMaskProvider");
  }
  return context;
};
